import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useActivity } from '../hooks/use-activities';
import { apiRequest } from '../lib/api';
import {
  buildActivityWhatsAppMessage,
  buildWhatsAppUrl,
} from '../lib/whatsapp';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { format } from 'date-fns';
import { es, enGB, it as itLocale, de, nb, nl } from 'date-fns/locale';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import RichText from '../components/RichText';
import { useT, useLanguage } from '../i18n/LanguageContext';
import { useLocalizedActivity } from '../i18n/contentLocale';
import { translatePriceUnit } from '../lib/priceUnit';
import { youtubeEmbedUrl } from '../lib/youtube';
import {
  Star, MapPin, Clock, Users, Calendar as CalendarIcon, Check, X,
  ShieldCheck, Award, Share2, Heart, MessageCircle,
} from 'lucide-react';
import ActivityGallery from '../components/ActivityGallery';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { activityJsonLd, breadcrumbJsonLd, faqPageJsonLd, buildLangAlternates, canonicalForLang } from '../lib/seo';

const DATE_FNS_LOCALES = { es, en: enGB, it: itLocale, de, no: nb, nl };

function buildTicketTypes(activity, t) {
  if (Array.isArray(activity?.ticketTypes) && activity.ticketTypes.length > 0) {
    return activity.ticketTypes;
  }

  const adultPrice = Number(activity?.price || 0);
  return [
    { id: 'adult', label: t('activityDetail.adults'), price: adultPrice, min: 1, max: 12, default: 2 },
    { id: 'child', label: t('activityDetail.children'), price: Number((adultPrice * 0.6).toFixed(2)), min: 0, max: 12, default: 0 },
  ];
}

function initialQuantities(activity, t) {
  return Object.fromEntries(buildTicketTypes(activity, t).map((ticket) => [ticket.id, ticket.default || 0]));
}

function priceDecimals(amount) {
  return Number(amount) % 1 ? 2 : 0;
}

export default function ActivityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = useT();
  const { lang, currentLanguage } = useLanguage();
  const { currency, format: formatPrice, formatText } = useCurrency();
  const { isSaved, toggle: toggleWishlist } = useWishlist();
  const { data: rawActivity, isLoading } = useActivity(id);
  // A renamed activity: the API resolved the old slug to its new canonical one.
  const redirectTo = rawActivity?.redirect_to;
  useEffect(() => {
    if (redirectTo) navigate(`/activity/${redirectTo}`, { replace: true });
  }, [redirectTo, navigate]);
  const activity = useLocalizedActivity(redirectTo ? null : rawActivity);

  const [date, setDate] = useState();
  const [quantities, setQuantities] = useState(() => initialQuantities(activity, t));
  const [timeSlot, setTimeSlot] = useState(activity?.timeSlots?.[0] || '10:00');
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);

  // Fetch real availability (free seats per slot + blackout) whenever the date
  // changes, so the widget shows live capacity and blocks full/blocked dates.
  useEffect(() => {
    if (!date || !activity?.id) { setAvailability(null); return undefined; }
    const iso = format(date, 'yyyy-MM-dd');
    let cancelled = false;
    setAvailLoading(true);
    apiRequest(`/api/activities/${activity.id}/availability`, { query: { date: iso } })
      .then((d) => { if (!cancelled) setAvailability(d); })
      .catch(() => { if (!cancelled) setAvailability(null); })
      .finally(() => { if (!cancelled) setAvailLoading(false); });
    return () => { cancelled = true; };
  }, [date, activity?.id]);

  useEffect(() => {
    setQuantities(initialQuantities(activity, t));
    setTimeSlot(activity?.timeSlots?.[0] || '10:00');
    setAcceptedTerms(false);
  }, [activity]);

  const ticketTypes = useMemo(() => buildTicketTypes(activity, t), [activity, t]);
  const ticketsPrice = useMemo(
    () => ticketTypes.reduce(
      (total, ticket) => total + ticket.price * (quantities[ticket.id] || 0),
      0
    ),
    [quantities, ticketTypes]
  );
  // Deposit model: some activities charge only a deposit online (rest paid on
  // site). depositTotal = sum of per-ticket deposits for the current selection.
  const depositTotal = useMemo(
    () => ticketTypes.reduce(
      (total, ticket) => total + (Number(ticket.deposit) || 0) * (quantities[ticket.id] || 0),
      0
    ),
    [quantities, ticketTypes]
  );
  const hasDeposit = depositTotal > 0;
  // How many guests the booking is for — drives the price of per-person add-ons.
  const totalGuests = useMemo(
    () => Object.values(quantities).reduce((sum, q) => sum + (q || 0), 0),
    [quantities]
  );
  const addons = useMemo(() => activity?.addons || [], [activity]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  // Drop any selected add-on when navigating to a different activity.
  useEffect(() => { setSelectedAddons([]); }, [activity?.id]);
  const toggleAddon = (addonId) =>
    setSelectedAddons((current) =>
      current.includes(addonId) ? current.filter((x) => x !== addonId) : [...current, addonId]
    );
  const addonLineTotal = (addon) => addon.price * (addon.per_person ? Math.max(1, totalGuests) : 1);
  const addonsTotal = useMemo(
    () => addons
      .filter((a) => selectedAddons.includes(a.id))
      .reduce((sum, a) => sum + addonLineTotal(a), 0),
    [addons, selectedAddons, totalGuests]
  );
  // Tickets + add-ons form the subtotal a coupon then discounts (mirrors backend).
  const totalPrice = ticketsPrice + addonsTotal;

  const [coupon, setCoupon] = useState({ code: '', discount: 0, applied: false, msg: '', checking: false });
  // If the cart changes, an applied coupon must be re-validated (percent amounts move).
  useEffect(() => {
    setCoupon((c) => (c.applied ? { ...c, applied: false, discount: 0 } : c));
  }, [totalPrice]);
  const applyCoupon = async () => {
    const code = coupon.code.trim();
    if (!code) return;
    setCoupon((c) => ({ ...c, checking: true, msg: '' }));
    try {
      const r = await apiRequest('/api/coupons/validate', {
        method: 'POST',
        body: JSON.stringify({ code, activity_id: activity.id, amount: totalPrice }),
      });
      setCoupon((c) => r.valid
        ? { ...c, applied: true, discount: r.discount, msg: '', checking: false }
        : { ...c, applied: false, discount: 0, msg: r.message || t('activityDetail.couponInvalid'), checking: false });
    } catch (e) {
      setCoupon((c) => ({ ...c, applied: false, discount: 0, msg: e.message || t('activityDetail.couponInvalid'), checking: false }));
    }
  };
  const finalPrice = Math.max(0, totalPrice - (coupon.applied ? coupon.discount : 0));

  if (isLoading) {
    return <div className="pt-40 pb-20 text-center text-gray-500">{t('common.loading')}</div>;
  }

  if (!activity) {
    return (
      <div className="pt-40 pb-20 text-center">
        <p>{t('activityDetail.notFound')}</p>
        <Link to="/activities" className="text-[#1fa5a3] underline">{t('activityDetail.back')}</Link>
      </div>
    );
  }

  const hasPublicApi = Boolean(process.env.REACT_APP_API_BASE_URL);
  const canPayOnline = activity.bookingEnabled === true && hasPublicApi;
  const paymentReady = activity.bookingEnabled === true;
  const timeSlots = activity.timeSlots?.length ? activity.timeSlots : ['09:00', '10:00', '11:30', '14:00', '15:30', '17:00'];
  const gallery = activity.gallery?.length ? activity.gallery : [activity.image];
  const videoEmbedUrl = youtubeEmbedUrl(activity.videoUrl);
  const providerUrl = activity.provider?.activityUrl;
  const cancellationPolicy = activity.cancellationPolicy || {
    short: 'Cancelacion sujeta a confirmacion del operador.',
    details: 'Contactaremos contigo para confirmar disponibilidad y condiciones finales antes de cerrar la reserva.',
    sourceUrl: providerUrl || '',
  };
  const pickupText = activity.pickup || t('activity.pickupDefault');
  const groupSize = activity.groupSize || t('activity.smallGroupDefault');
  const insurance = activity.insurance || t('activity.included');
  const languages = activity.languages || 'ES / EN';
  const restrictions = activity.restrictions || [];
  const hasReviews = activity.rating && activity.reviews > 0;

  // FAQPage JSON-LD built only from facts already shown elsewhere on this
  // page (duration, included/not-included, meeting point, cancellation,
  // requirements) — never a question without a real, visible answer.
  const faqItems = [
    activity.duration && { question: t('activityDetail.faqDuration'), answer: activity.duration },
    activity.included?.length && {
      question: t('activityDetail.faqIncluded'),
      answer: activity.included.map(formatText).join(', '),
    },
    activity.notIncluded?.length && {
      question: t('activityDetail.faqNotIncluded'),
      answer: activity.notIncluded.map(formatText).join(', '),
    },
    activity.meetingPoint && { question: t('activityDetail.faqMeetingPoint'), answer: activity.meetingPoint },
    cancellationPolicy.details && { question: t('activityDetail.faqCancellation'), answer: cancellationPolicy.details },
    restrictions.length > 0 && { question: t('activityDetail.faqRequirements'), answer: restrictions.join(' ') },
  ].filter(Boolean);

  const updateQuantity = (ticket, delta) => {
    setQuantities((current) => {
      const nextValue = Math.min(
        ticket.max,
        Math.max(ticket.min, (current[ticket.id] || 0) + delta)
      );
      return { ...current, [ticket.id]: nextValue };
    });
  };

  const updateCustomer = (field, value) => {
    setCustomer((current) => ({ ...current, [field]: value }));
  };

  const handleBook = async () => {
    if (!date) {
      toast({ title: t('activityDetail.tSelDateT'), description: t('activityDetail.tSelDateD') });
      return;
    }
    if (!timeSlot) {
      toast({ title: t('activityDetail.tSelTimeT'), description: t('activityDetail.tSelTimeD') });
      return;
    }
    if (ticketsPrice <= 0) {
      toast({ title: t('activityDetail.tSelPaxT'), description: t('activityDetail.tSelPaxD') });
      return;
    }

    if (!customer.name.trim() || !customer.email.trim() || !customer.phone.trim()) {
      toast({ title: t('activityDetail.tDataT'), description: t('activityDetail.tDataD') });
      return;
    }
    if (!acceptedTerms) {
      toast({ title: t('activityDetail.tAcceptT'), description: t('activityDetail.tAcceptD') });
      return;
    }

    if (!canPayOnline) {
      if (!paymentReady || hasPublicApi) {
        setIsBooking(true);
        try {
          await apiRequest('/api/booking-requests', {
            method: 'POST',
            body: JSON.stringify({
              activity_id: activity.id,
              service_date: format(date, 'yyyy-MM-dd'),
              time_slot: timeSlot,
              quantities,
              addon_ids: selectedAddons,
              customer: {
                name: customer.name.trim(),
                email: customer.email.trim(),
                phone: customer.phone.trim(),
              },
              accepted_terms: true,
            }),
          });
          toast({
            title: t('activityDetail.tSentT'),
            description: t('activityDetail.tSentD'),
          });
        } catch (error) {
          toast({
            title: t('activityDetail.tSentFailT'),
            description: error.message || t('activityDetail.tryAgain'),
            variant: 'destructive',
          });
        } finally {
          setIsBooking(false);
        }
        return;
      }

      toast({
        title: t('activityDetail.payPrep'),
        description: t('activityDetail.checkoutNoteReady'),
      });
      return;
    }

    setIsBooking(true);
    try {
      const checkout = await apiRequest('/api/payments/sumup/checkouts', {
        method: 'POST',
        body: JSON.stringify({
          activity_id: activity.id,
          service_date: format(date, 'yyyy-MM-dd'),
          time_slot: timeSlot,
          quantities,
          addon_ids: selectedAddons,
          coupon_code: coupon.applied ? coupon.code.trim() : undefined,
          customer: {
            name: customer.name.trim(),
            email: customer.email.trim(),
            phone: customer.phone.trim(),
          },
          accepted_terms: true,
        }),
      });

      window.location.assign(checkout.hosted_checkout_url);
    } catch (error) {
      toast({
        title: t('activityDetail.tPayFailT'),
        description: error.message || t('activityDetail.tryAgain'),
        variant: 'destructive',
      });
      setIsBooking(false);
    }
  };

  const handleWhatsApp = () => {
    const selectedDate = date
      ? format(date, 'd MMM yyyy', { locale: DATE_FNS_LOCALES[lang] || es })
      : '';
    const message = buildActivityWhatsAppMessage({
      activity,
      date: selectedDate,
      timeSlot,
      quantities,
    }, t);
    window.open(buildWhatsAppUrl(message), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="pt-24 pb-20 bg-white">
      <Seo
        title={`${activity.title} | OWA Wild Adventure`}
        description={activity.shortDescription || activity.description}
        image={activity.image}
        canonical={canonicalForLang(`/activity/${activity.id}`, lang)}
        alternates={buildLangAlternates(`/activity/${activity.id}`)}
        ogLocale={currentLanguage.locale.replace('-', '_')}
        type="product"
        robots={activity.noindex ? 'noindex,follow' : undefined}
      />
      <JsonLd data={activityJsonLd(activity, `/activity/${activity.id}`)} />
      <JsonLd data={breadcrumbJsonLd([
        { name: t('nav.home'), path: '/' },
        { name: t('nav.activities'), path: '/activities' },
        { name: activity.title, path: `/activity/${activity.id}` },
      ])} />
      <JsonLd data={faqPageJsonLd(faqItems)} />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-[#1fa5a3]">{t('nav.home')}</Link> / <Link to="/activities" className="hover:text-[#1fa5a3]">{t('nav.activities')}</Link> / <span className="text-[#14213d]">{activity.title}</span>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 mb-3">
            <MapPin className="w-4 h-4" /><span>{activity.location}</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <h1 className="text-3xl md:text-5xl font-bold text-[#14213d] leading-tight max-w-3xl">
              {activity.title}
            </h1>
            <div className="flex items-center gap-3">
              <button aria-label={t('activityDetail.share')} className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50"><Share2 className="w-4 h-4" /></button>
              <button
                type="button"
                onClick={() => toggleWishlist(activity.id)}
                aria-label={isSaved(activity.id) ? t('wishlist.remove') : t('wishlist.add')}
                aria-pressed={isSaved(activity.id)}
                className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50"
              >
                <Heart className={`w-4 h-4 ${isSaved(activity.id) ? 'fill-[#c8a25a] text-[#c8a25a]' : ''}`} />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 text-sm">
            {hasReviews ? (
              <div className="flex items-center gap-1.5">
                <Star className="w-4 h-4 fill-[#c8a25a] text-[#c8a25a]" />
                <span className="font-semibold text-[#14213d]">{activity.rating}</span>
                <span className="text-gray-500">({activity.reviews} {t('card.reviews')})</span>
              </div>
            ) : (
              <span className="font-medium text-[#1fa5a3]">{t('activityDetail.newActivity')}</span>
            )}
            {/* Each separator is glued to the item it introduces in one flex
                item, so flex-wrap can never strand a bullet alone at a
                line break (confirmed reproducible on mobile before this). */}
            <div className="flex items-center gap-1.5 text-gray-600">
              <span className="text-gray-300">•</span>
              <Clock className="w-4 h-4" /> {activity.duration}
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <span className="text-gray-300">•</span>
              <Award className="w-4 h-4" /> {paymentReady ? t('activityDetail.verified') : t('activityDetail.onRequest')}
            </div>
          </div>
        </div>

        <ActivityGallery key={activity.id} images={gallery} title={activity.title} />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10">
          <div>
            <Tabs defaultValue="desc" className="w-full">
              <TabsList className="bg-transparent p-0 h-auto border-b border-gray-200 rounded-none w-full max-w-full justify-start gap-6 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {['desc', 'incluido', 'punto', 'resenas'].map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className="px-0 py-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#1fa5a3] data-[state=active]:text-[#1fa5a3] data-[state=active]:shadow-none font-medium"
                  >
                    {tab === 'desc' && t('activityDetail.tabDesc')}
                    {tab === 'incluido' && t('activityDetail.tabIncluded')}
                    {tab === 'punto' && t('activityDetail.tabMeeting')}
                    {tab === 'resenas' && `${t('activityDetail.tabReviews')} (${activity.reviews || 0})`}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="desc" className="pt-8">
                <h3 className="text-2xl font-semibold mb-4">{t('activityDetail.about')}</h3>
                <RichText content={activity.description} className="max-w-none" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                  <div className="p-4 rounded-xl bg-[#f7f9fb]">
                    <Clock className="w-5 h-5 text-[#1fa5a3] mb-2" />
                    <div className="text-xs text-gray-500">{t('activity.duration')}</div>
                    <div className="font-semibold">{activity.duration}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-[#f7f9fb]">
                    <Users className="w-5 h-5 text-[#1fa5a3] mb-2" />
                    <div className="text-xs text-gray-500">{t('activityDetail.group')}</div>
                    <div className="font-semibold">{groupSize}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-[#f7f9fb]">
                    <ShieldCheck className="w-5 h-5 text-[#1fa5a3] mb-2" />
                    <div className="text-xs text-gray-500">{t('activityDetail.insurance')}</div>
                    <div className="font-semibold">{insurance}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-[#f7f9fb]">
                    <Award className="w-5 h-5 text-[#1fa5a3] mb-2" />
                    <div className="text-xs text-gray-500">{t('activity.languages')}</div>
                    <div className="font-semibold">{languages}</div>
                  </div>
                </div>

                {videoEmbedUrl ? (
                  <div className="mt-8">
                    <h4 className="font-semibold text-[#14213d] mb-3">{t('activityDetail.video')}</h4>
                    <div className="aspect-video rounded-2xl overflow-hidden">
                      <iframe
                        title={`${activity.title} — video`}
                        src={videoEmbedUrl}
                        className="w-full h-full border-0"
                        loading="lazy"
                        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                ) : null}

                <div className="mt-8 p-5 rounded-xl bg-[#f7f9fb]">
                  <h4 className="font-semibold text-[#14213d] mb-2">{t('activityDetail.cancelTitle')}</h4>
                  <p className="text-gray-600">{cancellationPolicy.details}</p>
                </div>

                {restrictions.length > 0 && (
                  <div className="mt-8">
                    <h4 className="font-semibold text-[#14213d] mb-4">{t('activityDetail.requirements')}</h4>
                    <ul className="space-y-3">
                      {restrictions.map((restriction) => (
                        <li key={restriction} className="flex items-start gap-2 text-gray-600">
                          <Check className="w-5 h-5 text-[#1fa5a3] flex-shrink-0 mt-0.5" /> {restriction}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="incluido" className="pt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold mb-4 text-[#14213d]">{t('activity.included')}</h4>
                    <ul className="space-y-3">
                      {(activity.included || []).map((item) => (
                        <li key={item} className="flex items-start gap-2 text-gray-600">
                          <Check className="w-5 h-5 text-[#1fa5a3] flex-shrink-0 mt-0.5" /> {formatText(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4 text-[#14213d]">{t('activityDetail.notIncludedH')}</h4>
                    <ul className="space-y-3">
                      {(activity.notIncluded || []).map((item) => (
                        <li key={item} className="flex items-start gap-2 text-gray-600">
                          <X className="w-5 h-5 text-[#c8a25a] flex-shrink-0 mt-0.5" /> {formatText(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="punto" className="pt-8">
                <h4 className="font-semibold mb-4 text-[#14213d]">{t('activityDetail.whereMeet')}</h4>
                <p className="text-gray-600 mb-2">{activity.meetingPoint}</p>
                <p className="text-sm text-gray-500 mb-4">{pickupText}</p>
                <div className="aspect-video rounded-2xl overflow-hidden">
                  <iframe
                    title="map"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(`${activity.meetingPoint}, Islas Canarias`)}&output=embed`}
                    className="w-full h-full border-0"
                    loading="lazy"
                  />
                </div>
              </TabsContent>

              <TabsContent value="resenas" className="pt-8">
                {hasReviews ? (
                  <div className="p-8 rounded-xl bg-[#f7f9fb] text-center">
                    <Star className="w-7 h-7 text-[#c8a25a] mx-auto mb-3" />
                    <h4 className="font-semibold text-[#14213d]">{activity.rating} {t('activityDetail.ratingOf5')}</h4>
                    <p className="text-gray-500 mt-2">{activity.reviews} {t('activityDetail.reviewsRefNote')}</p>
                  </div>
                ) : (
                  <div className="p-8 rounded-xl bg-[#f7f9fb] text-center">
                    <Star className="w-7 h-7 text-[#c8a25a] mx-auto mb-3" />
                    <h4 className="font-semibold text-[#14213d]">{t('activityDetail.newActivity')}</h4>
                    <p className="text-gray-500 mt-2">{t('activityDetail.newReviewsNote')}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_20px_50px_-15px_rgba(11,33,61,0.15)] p-6">
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-sm text-gray-500">{t('card.from')}</span>
                <span className="text-3xl font-bold text-[#1fa5a3]">{formatPrice(activity.price, { decimals: priceDecimals(activity.price) })}</span>
                <span className="text-sm text-gray-500">/ {translatePriceUnit(activity.priceUnit, lang) || t('card.perPerson')}</span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-1.5 block">{t('activityDetail.dateLabel')}</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="w-full flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-lg text-left hover:border-[#1fa5a3]">
                        <CalendarIcon className="w-4 h-4 text-[#1fa5a3]" />
                        <span className={date ? 'text-[#14213d]' : 'text-gray-400'}>
                          {date ? format(date, 'd MMM yyyy', { locale: DATE_FNS_LOCALES[lang] || es }) : t('activity.selectDate')}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-auto" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} locale={es}
                        disabled={(calendarDate) => calendarDate < new Date(new Date().setHours(0, 0, 0, 0))}
                        dayPrice={(d) => {
                          // Per-date price when available (admin/API), else the "from" price.
                          const iso = format(d, 'yyyy-MM-dd');
                          const raw = (activity.datePrices && activity.datePrices[iso]) ?? activity.price;
                          const n = Number(raw) || 0;
                          return n > 0 ? formatPrice(Math.round(n), { decimals: 0 }) : null;
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-1.5 block">{t('activityDetail.timeLabel')}</label>
                  {date && availability && availability.available === false && availability.reason === 'blackout' ? (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      {t('activityDetail.dateUnavailable')}
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {(availability?.slots?.length ? availability.slots : timeSlots.map((s) => ({ slot: s, available: null }))).map((s) => {
                        const slot = s.slot;
                        const free = s.available; // null = capacity not tracked
                        const soldOut = free === 0;
                        return (
                          <button
                            key={slot}
                            onClick={() => !soldOut && setTimeSlot(slot)}
                            disabled={soldOut}
                            className={`flex flex-col items-center py-2 rounded-lg text-sm font-medium border transition-all ${
                              soldOut
                                ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300 line-through'
                                : timeSlot === slot
                                  ? 'bg-[#1fa5a3] text-white border-[#1fa5a3]'
                                  : 'bg-white text-[#14213d] border-gray-200 hover:border-[#1fa5a3]'
                            }`}
                          >
                            <span>{slot}</span>
                            {free !== null ? (
                              <span className={`text-[10px] ${soldOut ? '' : timeSlot === slot ? 'text-white/80' : 'text-gray-400'}`}>
                                {soldOut ? t('activityDetail.soldOut') : t('activityDetail.seatsLeft', { count: free })}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {availLoading ? <p className="mt-1 text-xs text-gray-400">{t('common.loading')}</p> : null}
                </div>

                <div className={`grid gap-3 ${ticketTypes.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {ticketTypes.map((ticket) => {
                    const quantity = quantities[ticket.id] || 0;
                    return (
                      <div key={ticket.id}>
                        <label className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-1.5 block">
                          {ticket.label}
                        </label>
                        <div className="flex items-center border border-gray-200 rounded-lg">
                          <button
                            aria-label={`− ${ticket.label}`}
                            disabled={quantity <= ticket.min}
                            onClick={() => updateQuantity(ticket, -1)}
                            className="px-3 py-2 text-gray-500 hover:text-[#1fa5a3] disabled:opacity-30"
                          >-</button>
                          <span className="flex-1 text-center font-semibold">{quantity}</span>
                          <button
                            aria-label={`+ ${ticket.label}`}
                            disabled={quantity >= ticket.max}
                            onClick={() => updateQuantity(ticket, 1)}
                            className="px-3 py-2 text-gray-500 hover:text-[#1fa5a3] disabled:opacity-30"
                          >+</button>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1">{formatPrice(ticket.price, { decimals: priceDecimals(ticket.price) })} {t('activityDetail.each')}</p>
                      </div>
                    );
                  })}
                </div>

                {addons.length ? (
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-gray-500 tracking-wider block">
                      {t('activityDetail.addonsTitle')}
                    </label>
                    {addons.map((addon) => {
                      const checked = selectedAddons.includes(addon.id);
                      return (
                        <label
                          key={addon.id}
                          className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition ${checked ? 'border-[#1fa5a3] bg-[#1fa5a3]/5' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                          <Checkbox checked={checked} onCheckedChange={() => toggleAddon(addon.id)} />
                          <span className="flex-1 text-sm text-gray-700">{addon.label}</span>
                          <span className="text-sm font-semibold text-[#14213d] whitespace-nowrap">
                            +{formatPrice(addon.price, { decimals: priceDecimals(addon.price) })}
                            {addon.per_person ? <span className="ml-1 text-[11px] font-normal text-gray-400">{t('activityDetail.addonPerPerson')}</span> : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}

                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label htmlFor="booking-name" className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-1.5 block">{t('activityDetail.fullName')}</label>
                    <input id="booking-name" value={customer.name} onChange={(event) => updateCustomer('name', event.target.value)} autoComplete="name" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-[#1fa5a3]" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="booking-email" className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-1.5 block">Email</label>
                      <input id="booking-email" type="email" value={customer.email} onChange={(event) => updateCustomer('email', event.target.value)} autoComplete="email" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-[#1fa5a3]" />
                    </div>
                    <div>
                      <label htmlFor="booking-phone" className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-1.5 block">{t('activityDetail.phone')}</label>
                      <input id="booking-phone" type="tel" value={customer.phone} onChange={(event) => updateCustomer('phone', event.target.value)} autoComplete="tel" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-[#1fa5a3]" />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-2 text-sm">
                  {ticketTypes.map((ticket) => {
                    const quantity = quantities[ticket.id] || 0;
                    if (!quantity) return null;
                    return (
                      <div key={ticket.id} className="flex justify-between text-gray-600">
                        <span>{formatPrice(ticket.price, { decimals: priceDecimals(ticket.price) })} × {quantity} {ticket.label.toLowerCase()}</span>
                        <span>{formatPrice(ticket.price * quantity, { decimals: priceDecimals(ticket.price * quantity) })}</span>
                      </div>
                    );
                  })}
                  {addons.filter((a) => selectedAddons.includes(a.id)).map((addon) => (
                    <div key={addon.id} className="flex justify-between text-gray-600">
                      <span>
                        {addon.label}
                        {addon.per_person ? ` × ${Math.max(1, totalGuests)}` : ''}
                      </span>
                      <span>{formatPrice(addonLineTotal(addon), { decimals: priceDecimals(addonLineTotal(addon)) })}</span>
                    </div>
                  ))}
                  {paymentReady ? (
                    <div className="pt-2">
                      <div className="flex gap-2">
                        <input
                          value={coupon.code}
                          onChange={(e) => setCoupon((c) => ({ ...c, code: e.target.value.toUpperCase(), msg: '' }))}
                          placeholder={t('activityDetail.couponPlaceholder')}
                          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm uppercase outline-none focus:border-[#1fa5a3]"
                        />
                        <button type="button" onClick={applyCoupon} disabled={coupon.checking || !coupon.code.trim()}
                          className="rounded-lg border border-[#1fa5a3] px-3 py-2 text-sm font-semibold text-[#1fa5a3] transition hover:bg-[#1fa5a3] hover:text-white disabled:opacity-50">
                          {coupon.checking ? '…' : t('activityDetail.couponApply')}
                        </button>
                      </div>
                      {coupon.msg ? <p className="mt-1 text-xs text-red-600">{coupon.msg}</p> : null}
                    </div>
                  ) : null}

                  {coupon.applied ? (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>{t('activityDetail.couponDiscount')} ({coupon.code})</span>
                      <span>−{formatPrice(coupon.discount, { decimals: priceDecimals(coupon.discount) })}</span>
                    </div>
                  ) : null}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-100">
                    <span className="text-[#14213d]">{t('activity.total')}</span>
                    <span className="text-[#1fa5a3]">
                      {coupon.applied ? <span className="mr-2 text-sm font-normal text-gray-400 line-through">{formatPrice(totalPrice, { decimals: priceDecimals(totalPrice) })}</span> : null}
                      {formatPrice(finalPrice, { decimals: priceDecimals(finalPrice) })}
                    </span>
                  </div>
                  {hasDeposit && ticketsPrice > 0 ? (
                    <div className="mt-2 rounded-xl border border-[#1fa5a3]/30 bg-[#1fa5a3]/5 p-3 space-y-1">
                      <div className="flex justify-between font-semibold text-[#14213d]">
                        <span>{t('activityDetail.payNowDeposit')}</span>
                        <span className="text-[#1fa5a3]">{formatPrice(depositTotal, { decimals: priceDecimals(depositTotal) })}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{t('activityDetail.balanceOnBoard')}</span>
                        <span>{formatPrice(Math.max(0, finalPrice - depositTotal), { decimals: priceDecimals(finalPrice - depositTotal) })}</span>
                      </div>
                    </div>
                  ) : null}
                  {paymentReady && currency !== (activity.paymentCurrency || 'EUR') && (
                    <p className="text-xs text-gray-500">{t('activityDetail.amountEurNote')}</p>
                  )}
                </div>

                <label htmlFor="accept-cancellation" className="flex items-start gap-2 cursor-pointer text-xs text-gray-600 leading-relaxed">
                  <Checkbox id="accept-cancellation" checked={acceptedTerms} onCheckedChange={(checked) => setAcceptedTerms(checked === true)} className="mt-0.5" />
                  <span>{t('activityDetail.acceptPolicy')} {cancellationPolicy.short}</span>
                </label>

                <button
                  onClick={handleBook}
                  disabled={isBooking}
                  className="w-full py-3.5 bg-[#c8a25a] hover:bg-[#b08c49] text-white rounded-full font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {canPayOnline
                    ? (isBooking ? t('activityDetail.openingSumup') : t('activityDetail.payingSumup'))
                    : (paymentReady ? t('activityDetail.payPrep') : (isBooking ? t('activityDetail.sendingRequest') : t('activityDetail.requestBooking')))}
                </button>
                <button
                  type="button"
                  onClick={handleWhatsApp}
                  className={`w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold transition-all ${
                    canPayOnline || paymentReady
                      ? 'border border-[#25D366] text-[#168f43] hover:bg-[#ecfff2]'
                      : 'bg-[#25D366] hover:bg-[#20bd5a] text-white'
                  }`}
                >
                  <MessageCircle className="w-5 h-5" />
                  {canPayOnline || paymentReady
                    ? t('activityDetail.whatsappDoubts')
                    : t('activityDetail.confirmWhatsApp')}
                </button>
                <p className="text-xs text-center text-gray-500">
                  {canPayOnline
                    ? `${cancellationPolicy.short} · ${t('activityDetail.payTotalEur')}`
                    : (paymentReady
                      ? t('activityDetail.checkoutNoteReady')
                      : t('activityDetail.checkoutNoteRequest'))}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
